import React, { useState, useEffect, useCallback } from 'react';
import { getProductos, deleteProducto } from '../../../api/productos';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, TextField, CircularProgress, TablePagination, Box } from '@mui/material';
import { Edit, Delete, Visibility, Add, Update } from '@mui/icons-material';
import CreateProducto from './_Create';
import EditProducto from './_Edit';
import ViewProducto from './_ViewById';
import { ConfirmDelete } from '../../../components/ConfirmDelete';
import { getCategorias } from '../../../api/categorias';
import { getUnidades } from '../../../api/unidades';
import { useAuth } from '../../../context/auth';

const Productos = () => {
  const [productos, setProductos] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [selectedProducto, setSelectedProducto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { permisos } = useAuth();
  const { Prm_Config_Prod_Crear, Prm_Config_Prod_Modificar, Prm_Config_Prod_Eliminar } = permisos;
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [lstCategorias, setLstCategorias] = useState<any[]>([]);
  const [lstUnidades, setLstUnidades] = useState<any[]>([]);

  const handleOpenDeleteConfirm = useCallback((id: number) => {
    setDeleteId(id);
    setOpenConfirmDelete(true);
  }, []);

  const handleCloseDeleteConfirm = useCallback(() => {
    setOpenConfirmDelete(false);
    setDeleteId(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deleteId !== null) {
      await deleteProducto(deleteId);
      fetchProductos();
      handleCloseDeleteConfirm();
    }
  }, [deleteId, handleCloseDeleteConfirm]);

  useEffect(() => {
    fetchProductos();
    fetchCategoriasYUnidades();
  }, []);

  const fetchProductos = useCallback(async () => {
    setLoading(true);
    const response = await getProductos();
    if (response.isOk) {
      setProductos(response.data);
    }
    setLoading(false);
  }, []);

  const fetchCategoriasYUnidades = useCallback(async () => {
    setLoading(true);
    const response = await getCategorias();
    const responseUnidades = await getUnidades();
    if (response.isOk && responseUnidades.isOk) {
      setLstCategorias(response.data);
      setLstUnidades(responseUnidades.data);
    }
    setLoading(false);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const filteredProductos = productos.filter((producto: any) => producto.Pr_Nombre.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div style={{ marginTop: '5px' }}>
      {loading ? (
        <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
          <CircularProgress />
        </Box>
      ) : (
        <div style={{ marginTop: '5px' }}>
          <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
            <TextField label='Buscar' value={searchTerm} onChange={handleSearchChange} fullWidth size='small' />
            {Prm_Config_Prod_Crear === 1 && (
              <IconButton onClick={() => setOpenCreate(true)} title='Nuevo' className='tool-btn'>
                <Add color='success' />
              </IconButton>
            )}
            <IconButton onClick={fetchProductos} title='Actualizar' className='tool-btn'>
              <Update />
            </IconButton>
          </Box>
          <TableContainer component={Paper}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell>Categoría</TableCell>
                  <TableCell>Unidad</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProductos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((producto: any) => (
                  <TableRow key={producto.Pr_Id}>
                    <TableCell>{producto.Pr_Id}</TableCell>
                    <TableCell>{producto.Pr_Nombre}</TableCell>
                    <TableCell>{producto.Pr_Codigo}</TableCell>
                    <TableCell>{lstCategorias.find((categoria: any) => categoria.Ct_Id === producto.Pr_Ct_Id)?.Ct_Nombre}</TableCell>
                    <TableCell>{lstUnidades.find((unidad: any) => unidad.Un_Id === producto.Pr_Un_Id)?.Un_Nombre}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setSelectedProducto(producto);
                          setOpenView(true);
                        }}>
                        <Visibility color='info' />
                      </IconButton>
                      {Prm_Config_Prod_Modificar === 1 && (
                        <IconButton
                          onClick={() => {
                            setSelectedProducto(producto);
                            setOpenEdit(true);
                          }}>
                          <Edit color='primary' />
                        </IconButton>
                      )}
                      {Prm_Config_Prod_Eliminar === 1 && (
                        <IconButton onClick={() => handleOpenDeleteConfirm(producto.Pr_Id)} title='Eliminar'>
                          <Delete color='error' />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component='div' count={filteredProductos.length} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleChangeRowsPerPage} />
        </div>
      )}

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)}>
        <DialogTitle>Crear Producto</DialogTitle>
        <DialogContent>
          <CreateProducto
            lstCategorias={lstCategorias}
            lstUnidades={lstUnidades}
            onClose={() => {
              setOpenCreate(false);
              fetchProductos();
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>Editar Producto</DialogTitle>
        <DialogContent>
          <EditProducto
            lstCategorias={lstCategorias}
            lstUnidades={lstUnidades}
            producto={selectedProducto}
            onClose={() => {
              setOpenEdit(false);
              fetchProductos();
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={openView} onClose={() => setOpenView(false)}>
        <DialogTitle>Ver Producto</DialogTitle>
        <DialogContent>
          <ViewProducto producto={selectedProducto} onClose={() => setOpenView(false)} />
        </DialogContent>
      </Dialog>

      <ConfirmDelete open={openConfirmDelete} onClose={handleCloseDeleteConfirm} onConfirm={handleConfirmDelete} message='¿Estás seguro de que deseas eliminar este producto?' />
    </div>
  );
};

export default Productos;
